import React, { useState, useEffect, useRef, useCallback } from 'react'
import Lightbox, { PLUGIN_ZOOM, ACTION_CLOSE } from 'yet-another-react-lightbox'
import Zoom from 'yet-another-react-lightbox/plugins/zoom'
import { RotateCcw, RotateCw } from 'lucide-react'
import 'yet-another-react-lightbox/styles.css'

export interface ImageLightboxSlide {
  src: string
  title?: string
  description?: string
}

export interface ImageLightboxProps {
  open: boolean
  close: () => void
  slides: (ImageLightboxSlide | string)[]
  index?: number
}

const ImageLightbox: React.FC<ImageLightboxProps> = ({
  open,
  close,
  slides,
  index = 0,
}) => {
  const [rotation, setRotation] = useState(0)
  const [scale, setScale] = useState(1)
  const zoomRef = useRef<any>(null)

  const normalizedSlides = slides
    .filter((s): s is ImageLightboxSlide | string => Boolean(s))
    .map((s) => (typeof s === 'string' ? { src: s } : s))
    .filter((s) => Boolean(s.src))

  const calculateScale = useCallback((currentRotation: number) => {
    if (currentRotation % 180 === 0) {
      setScale(1)
      return
    }
    const img = document.querySelector('.yarl__slide_current .yarl__slide_image') as HTMLImageElement | null
    const container = document.querySelector('.yarl__slide_current') as HTMLElement | null
    if (img && container) {
      const imgWidth = img.naturalWidth || img.clientWidth
      const imgHeight = img.naturalHeight || img.clientHeight
      const containerWidth = container.clientWidth
      const containerHeight = container.clientHeight
      if (imgWidth && imgHeight && containerWidth && containerHeight) {
        const availableWidth = containerWidth - 32
        const availableHeight = containerHeight - 64
        const scaleX = availableWidth / imgHeight
        const scaleY = availableHeight / imgWidth
        setScale(Math.max(0.1, Math.min(scaleX, scaleY, 1)))
        return
      }
    }
    setScale(1)
  }, [])

  const handleRotateCw = () => {
    if (zoomRef.current && typeof zoomRef.current.changeZoom === 'function' && zoomRef.current.zoom > 1) {
      zoomRef.current.changeZoom(1)
    }
    setRotation((prev) => (prev + 90) % 360)
  }

  const handleRotateCcw = () => {
    if (zoomRef.current && typeof zoomRef.current.changeZoom === 'function' && zoomRef.current.zoom > 1) {
      zoomRef.current.changeZoom(1)
    }
    setRotation((prev) => (prev - 90 + 360) % 360)
  }

  const handleView = useCallback(() => {
    setRotation(0)
    setScale(1)
  }, [])

  const handleClose = () => {
    setRotation(0)
    setScale(1)
    close()
  }

  useEffect(() => {
    calculateScale(rotation)
    const timer = setTimeout(() => calculateScale(rotation), 50)
    return () => clearTimeout(timer)
  }, [calculateScale, rotation])

  useEffect(() => {
    const handleResize = () => calculateScale(rotation)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [calculateScale, rotation])

  useEffect(() => {
    if (!open) {
      setRotation(0)
      setScale(1)
    }
  }, [open])

  if (!open || normalizedSlides.length === 0) return null

  return (
    <>
      <style>{`
        .yarl__portal {
          z-index: 1000002 !important;
        }
        .yarl__button {
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
        }
        .yarl__slide_image {
          transition: transform 0.25s ease !important;
        }
        ${rotation !== 0 ? `
        .yarl__slide_current .yarl__slide_image {
          transform: rotate(${rotation}deg) scale(${scale}) !important;
        }
        ` : ''}
      `}</style>
      <Lightbox
        open={open}
        close={handleClose}
        index={Math.max(0, Math.min(index, normalizedSlides.length - 1))}
        slides={normalizedSlides}
        plugins={[Zoom]}
        portal={{ root: typeof document !== 'undefined' ? document.body : undefined }}
        styles={{
          root: { zIndex: 1000002, '--yarl__portal_zindex': '1000002' } as any,
        }}
        toolbar={{
          buttons: [
            PLUGIN_ZOOM,
            <button
              key="rotate-ccw"
              type="button"
              className="yarl__button"
              title="Girar 90° à esquerda"
              aria-label="Girar 90° à esquerda"
              onClick={handleRotateCcw}
            >
              <RotateCcw className="yarl__icon" size={24} />
            </button>,
            <button
              key="rotate-cw"
              type="button"
              className="yarl__button"
              title="Girar 90° à direita"
              aria-label="Girar 90° à direita"
              onClick={handleRotateCw}
            >
              <RotateCw className="yarl__icon" size={24} />
            </button>,
            ACTION_CLOSE,
          ],
        }}
        zoom={{
          ref: zoomRef,
          maxZoomPixelRatio: 4,
          zoomInMultiplier: 2,
          doubleTapDelay: 300,
          doubleClickDelay: 300,
          doubleClickMaxStops: 2,
          keyboardMoveDistance: 50,
          wheelZoomDistanceFactor: 100,
          pinchZoomDistanceFactor: 100,
          scrollToZoom: true,
        }}
        animation={{ fade: 250, swipe: 250 }}
        carousel={{ finite: normalizedSlides.length <= 1 }}
        on={{ view: handleView }}
        render={{
          buttonPrev: normalizedSlides.length <= 1 ? () => null : undefined,
          buttonNext: normalizedSlides.length <= 1 ? () => null : undefined,
        }}
      />
    </>
  )
}

export default ImageLightbox
